/**
 * IndividualCreativeFunnelSection.tsx
 *
 * Drill-down into a single ad creative with full funnel + performance insights.
 *
 * Layout:
 *   - Toggle: SINGLE | ALL TOP 8
 *   - SINGLE mode:
 *       Dropdown selector → Trapezoid funnel → Section 2: Insights
 *   - ALL TOP 8 mode:
 *       Compact funnel grid for top 8 ads + efficiency table
 */

import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { branding } from "../../../../config/branding";
import type {
  IndividualCreativeFunnelRow,
  IndividualCreativeAnalysis,
  AdOption,
  BenchmarkRow,
  DropOffInfo,
  PerformanceClass,
  Decision,
  CreativeDailyPoint,
} from "../individualCreativeBreakdown";
import {
  getAdOptions,
  getIndividualCreativeAnalysis,
  getTopNAdsData,
} from "../individualCreativeBreakdown";
import type { MetaLeadGenRow, MetaLeadGenFilters } from "../types";

// ─────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────
const fmtPct   = (v: number) => v === 0 ? "N/A" : `${v.toFixed(1)}%`;
const fmtCur   = (v: number) => v === 0 ? "N/A" : `$${v.toFixed(2)}`;
const fmtCount = (v: number) => {
  if (v === 0) return "0";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
  return String(Math.round(v));
};
const fmtCurK  = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;

// ─────────────────────────────────────────────
// COLORS
// ─────────────────────────────────────────────
const C = branding.colors;
const COLOR_LINE_1 = C.chart[0];
const COLOR_LINE_2 = C.chart[1];

// ─────────────────────────────────────────────
// FUNNEL STAGES
// ─────────────────────────────────────────────
const FUNNEL_STAGES = [
  { key: "impressions"  as const, label: "Impressions" },
  { key: "clicks"       as const, label: "Clicks" },
  { key: "optins"       as const, label: "Opt-ins" },
  { key: "applications" as const, label: "Applications" },
  { key: "closed"       as const, label: "Closed" },
];

type StageKey = (typeof FUNNEL_STAGES)[number]["key"];

const RATE_BETWEEN: Array<{ from: StageKey; to: StageKey; rateKey: keyof IndividualCreativeFunnelRow; label: string }> = [
  { from: "impressions",   to: "clicks",       rateKey: "ctr",       label: "CTR" },
  { from: "clicks",        to: "optins",        rateKey: "optinRate", label: "Opt-in Rate" },
  { from: "optins",        to: "applications",  rateKey: "appRate",   label: "App Rate" },
  { from: "applications",  to: "closed",        rateKey: "closeRate", label: "Close Rate" },
];

// ─────────────────────────────────────────────
// CLASSIFICATION CONFIG
// ─────────────────────────────────────────────
const CLASS_CONFIG: Record<PerformanceClass, { emoji: string; bg: string; text: string; border: string }> = {
  "Strong Performer":      { emoji: "🚀", bg: "bg-green-500/10",  text: "text-green-400",  border: "border-green-500/30" },
  "Clickbait Risk":        { emoji: "⚠️", bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  "High Intent, Weak Hook":{ emoji: "🎯", bg: "bg-blue-500/10",   text: "text-blue-400",   border: "border-blue-500/30" },
  "Hook Works, Funnel Leaks":{ emoji: "🔧", bg: "bg-yellow-500/10",text: "text-yellow-400", border: "border-yellow-500/30" },
  "Low Volume":            { emoji: "📊", bg: "bg-muted/40",      text: "text-muted-foreground", border: "border-border" },
  "Needs Work":            { emoji: "🔴", bg: "bg-red-500/10",    text: "text-red-400",    border: "border-red-500/30" },
};

const DECISION_CONFIG: Record<Decision, { bg: string; text: string; border: string; pill: string }> = {
  "Scale": { bg: "bg-green-500/15", text: "text-green-300", border: "border-green-500/40", pill: "bg-green-500" },
  "Fix":   { bg: "bg-yellow-500/15",text: "text-yellow-300",border: "border-yellow-500/40",pill: "bg-yellow-500" },
  "Kill":  { bg: "bg-red-500/15",   text: "text-red-300",   border: "border-red-500/40",  pill: "bg-red-500" },
};

// ─────────────────────────────────────────────
// TRAPEZOID FUNNEL (single ad)
// ─────────────────────────────────────────────
function TrapezoidFunnel({ row }: { row: IndividualCreativeFunnelRow }) {
  const maxVal = row.impressions;

  function widthPct(stageKey: StageKey): number {
    if (maxVal === 0) return 0;
    const val = row[stageKey] as number;
    // Min 8% so there's always a visible bar when value > 0
    const raw = safeDivide(val, maxVal) * 85; // max 85% so bars look proportional
    return val > 0 ? Math.max(raw, 8) : 0;
  }

  return (
    <div className="flex flex-col items-center gap-0 w-full max-w-sm mx-auto">
      {FUNNEL_STAGES.map((stage, i) => {
        const rateInfo = i > 0 ? RATE_BETWEEN[i - 1] : null;
        const rateVal  = rateInfo ? (row[rateInfo.rateKey] as number) : null;
        const width    = widthPct(stage.key);
        const count    = row[stage.key] as number;

        return (
          <React.Fragment key={stage.key}>
            {/* Conversion rate badge between bars */}
            {rateInfo && rateVal !== null && (
              <div className="flex flex-col items-center py-2">
                <div className="w-px h-3 bg-border/60" />
                <div
                  className="px-3 py-1 rounded-full border flex items-center gap-1.5 my-1"
                  style={{
                    background:   "var(--card)",
                    borderColor:  `${row.color}40`,
                  }}
                >
                  <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                    {rateInfo.label}
                  </span>
                  <span className="text-[11px] font-bold" style={{ color: row.color }}>
                    {fmtPct(rateVal)}
                  </span>
                </div>
                <div className="w-px h-3 bg-border/60" />
              </div>
            )}

            {/* Bar */}
            <div className="w-full flex flex-col items-center gap-1.5">
              <div className="w-full flex justify-center">
                <div
                  className="h-10 rounded-lg flex items-center justify-center transition-all duration-500 relative"
                  style={{
                    width:      `${width}%`,
                    background: `linear-gradient(135deg, ${row.color}cc, ${row.color}88)`,
                    minWidth:   count > 0 ? "40px" : "0px",
                    boxShadow:  count > 0 ? `0 0 12px ${row.color}30` : "none",
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">
                  {stage.label}
                </span>
                <span className="text-[15px] font-bold text-foreground">{fmtCount(count)}</span>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// KPI STRIP
// ─────────────────────────────────────────────
function KpiStrip({ row }: { row: IndividualCreativeFunnelRow }) {
  const kpis = [
    { label: "CTR",          value: fmtPct(row.ctr) },
    { label: "Opt-in Rate",  value: fmtPct(row.optinRate) },
    { label: "App Rate",     value: fmtPct(row.appRate) },
    { label: "Close Rate",   value: fmtPct(row.closeRate) },
    { label: "Cost / Optin", value: fmtCur(row.costPerOptin) },
    { label: "Cost / App",   value: fmtCur(row.costPerApp) },
  ];

  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
      {kpis.map(kpi => (
        <div
          key={kpi.label}
          className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 flex flex-col gap-0.5"
        >
          <span className="text-[9px] text-muted-foreground uppercase tracking-widest font-semibold">
            {kpi.label}
          </span>
          <span className="text-[13px] font-bold text-foreground">{kpi.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// BENCHMARK TABLE
// ─────────────────────────────────────────────
interface BenchmarkTableProps {
  creative:   IndividualCreativeFunnelRow;
  accountAvg: BenchmarkRow;
  typeAvg:    BenchmarkRow;
}

function BenchmarkTable({ creative, accountAvg, typeAvg }: BenchmarkTableProps) {
  type MetricDef = {
    label: string;
    creativeVal: number;
    accountVal:  number;
    typeVal:     number;
    fmt:         (v: number) => string;
    higherBetter: boolean;
  };

  const metrics: MetricDef[] = [
    { label: "CTR",         creativeVal: creative.ctr,          accountVal: accountAvg.ctr,          typeVal: typeAvg.ctr,          fmt: fmtPct, higherBetter: true },
    { label: "Opt-in Rate", creativeVal: creative.optinRate,    accountVal: accountAvg.optinRate,    typeVal: typeAvg.optinRate,    fmt: fmtPct, higherBetter: true },
    { label: "App Rate",    creativeVal: creative.appRate,       accountVal: accountAvg.appRate,       typeVal: typeAvg.appRate,       fmt: fmtPct, higherBetter: true },
    { label: "Close Rate",  creativeVal: creative.closeRate,     accountVal: accountAvg.closeRate,     typeVal: typeAvg.closeRate,     fmt: fmtPct, higherBetter: true },
    { label: "Cost/Optin",  creativeVal: creative.costPerOptin,  accountVal: accountAvg.costPerOptin,  typeVal: typeAvg.costPerOptin,  fmt: fmtCur, higherBetter: false },
    { label: "Cost/App",    creativeVal: creative.costPerApp,    accountVal: accountAvg.costPerApp,    typeVal: typeAvg.costPerApp,    fmt: fmtCur, higherBetter: false },
  ];

  function diffClass(val: number, avg: number, higherBetter: boolean): string {
    if (avg === 0) return "text-muted-foreground";
    const better = higherBetter ? val >= avg : val <= avg;
    return better ? "text-green-400" : "text-red-400";
  }

  function diffBadge(val: number, avg: number, higherBetter: boolean): string {
    if (avg === 0) return "";
    const pct = ((val - avg) / avg) * 100;
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${pct.toFixed(0)}%`;
  }

  const cols = [
    { header: "Metric",            key: "metric" },
    { header: "This Creative",     key: "creative" },
    { header: "vs Account Avg",    key: "account" },
    { header: `vs Type Avg`,       key: "type" },
  ];

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border bg-muted/20">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
          Benchmark Comparison
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60">
              {cols.map(c => (
                <th key={c.key} className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m, i) => (
              <tr key={m.label} className={i < metrics.length - 1 ? "border-b border-border/40" : ""}>
                <td className="px-4 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {m.label}
                </td>
                <td className="px-4 py-2.5 text-[13px] font-bold text-foreground">
                  {m.fmt(m.creativeVal)}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">{m.fmt(m.accountVal)}</span>
                    {m.accountVal > 0 && (
                      <span className={`text-[11px] font-bold ${diffClass(m.creativeVal, m.accountVal, m.higherBetter)}`}>
                        {diffBadge(m.creativeVal, m.accountVal, m.higherBetter)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">{m.fmt(m.typeVal)}</span>
                    {m.typeVal > 0 && (
                      <span className={`text-[11px] font-bold ${diffClass(m.creativeVal, m.typeVal, m.higherBetter)}`}>
                        {diffBadge(m.creativeVal, m.typeVal, m.higherBetter)}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DROP-OFF ANALYSIS
// ─────────────────────────────────────────────
function DropOffAnalysis({ dropOffs }: { dropOffs: DropOffInfo[] }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border bg-muted/20">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
          Drop-off Analysis
        </span>
      </div>
      <div className="p-4 flex flex-col gap-2">
        {dropOffs.map(d => (
          <div key={d.fromLabel} className="flex items-center gap-3">
            {/* Badge */}
            <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
              d.isWorst  ? "bg-red-500/20 text-red-400 border border-red-500/40" :
              d.isSecond ? "bg-orange-500/20 text-orange-400 border border-orange-500/40" :
              "bg-muted/40 text-muted-foreground border border-border"
            }`}>
              {d.isWorst ? "1" : d.isSecond ? "2" : "·"}
            </div>

            {/* Stage label */}
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">
              {d.fromLabel} → {d.toLabel}
            </span>

            {/* Progress bar */}
            <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  d.isWorst ? "bg-red-400" : d.isSecond ? "bg-orange-400" : "bg-border"
                }`}
                style={{ width: `${d.dropPct}%` }}
              />
            </div>

            {/* Drop % */}
            <span className={`text-[12px] font-bold whitespace-nowrap ${
              d.isWorst ? "text-red-400" : d.isSecond ? "text-orange-400" : "text-muted-foreground"
            }`}>
              {d.dropPct.toFixed(1)}% drop
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PERFORMANCE CLASSIFICATION + DECISION BOX
// ─────────────────────────────────────────────
function ClassificationAndDecision({ analysis }: { analysis: IndividualCreativeAnalysis }) {
  const { classification, decision } = analysis;
  const cls = CLASS_CONFIG[classification];
  const dec = DECISION_CONFIG[decision.verdict];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Classification */}
      <div className={`rounded-xl border ${cls.border} ${cls.bg} p-4 flex flex-col gap-2`}>
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
          Performance Classification
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xl leading-none">{cls.emoji}</span>
          <span className={`text-[15px] font-bold ${cls.text}`}>{classification}</span>
        </div>
      </div>

      {/* Decision box */}
      <div className={`rounded-xl border ${dec.border} ${dec.bg} p-4 flex flex-col gap-2`}>
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
          Recommendation
        </span>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-[13px] font-bold text-white ${dec.pill}`}>
            {decision.verdict}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">{decision.reason}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MINI TREND CHART
// ─────────────────────────────────────────────
function MiniTrendChart({ series, color }: { series: CreativeDailyPoint[]; color: string }) {
  const TICK_STYLE = { fontSize: 10, fill: "var(--muted-foreground)", fontFamily: "inherit" };
  const AXIS_LINE  = { stroke: "var(--border)" };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">
        Daily Trend
      </p>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={series} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
          <XAxis
            dataKey="date"
            tick={TICK_STYLE}
            axisLine={AXIS_LINE}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={TICK_STYLE}
            axisLine={false}
            tickLine={false}
            width={30}
          />
          <Tooltip
            contentStyle={{
              background:   "var(--popover)",
              border:       "1px solid var(--border)",
              borderRadius: "8px",
              fontSize:     "11px",
            }}
          />
          <Line
            type="monotone"
            dataKey="optins"
            name="Opt-ins"
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="apps"
            name="Applications"
            stroke={COLOR_LINE_2}
            strokeWidth={1.5}
            strokeDasharray="4 2"
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      {/* Legend */}
      <div className="flex gap-4 mt-2">
        <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <span className="inline-block w-4 h-[2px] rounded-full" style={{ background: color }} />
          Opt-ins
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <span className="inline-block w-4 h-[2px] rounded-full" style={{ background: COLOR_LINE_2, opacity: 0.7 }} />
          Applications
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SINGLE MODE — full drill-down
// ─────────────────────────────────────────────
interface SingleModeProps {
  adOptions:   AdOption[];
  rows:        MetaLeadGenRow[];
  filters:     MetaLeadGenFilters;
  closeAttribution?: import("../useCloseCreativeAttribution").CloseCreativeMap;
}

function SingleMode({ adOptions, rows, filters, closeAttribution }: SingleModeProps) {
  const [selectedAdId, setSelectedAdId] = useState<string>(adOptions[0]?.adId ?? "");

  const analysis = useMemo(() => {
    if (!selectedAdId) return null;
    return getIndividualCreativeAnalysis(rows, filters, selectedAdId, closeAttribution);
  }, [rows, filters, selectedAdId, closeAttribution]);

  if (adOptions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        No ads found for the current filters.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dropdown selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
          Select Creative
        </span>
        <div className="relative flex-1 max-w-md">
          <select
            value={selectedAdId}
            onChange={e => setSelectedAdId(e.target.value)}
            className="w-full appearance-none rounded-lg border border-border bg-card px-3 py-2 pr-8 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/40 cursor-pointer"
          >
            {adOptions.map(opt => (
              <option key={opt.adId} value={opt.adId}>
                {opt.shortName}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">▼</div>
        </div>

        {/* Type badge */}
        {analysis && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold whitespace-nowrap"
            style={{
              background:  `${analysis.funnel.color}15`,
              borderColor: `${analysis.funnel.color}40`,
              color:        analysis.funnel.color,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: analysis.funnel.color }} />
            {analysis.funnel.typeLabel}
          </div>
        )}
      </div>

      {!analysis && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Select an ad to see its funnel analysis.
        </div>
      )}

      {analysis && (
        <>
          {/* ── SECTION 1: Funnel ── */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h4 className="text-sm font-bold text-foreground">{analysis.funnel.shortName}</h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Spend: {analysis.funnel.spend > 0 ? `$${analysis.funnel.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "N/A"}
                  {" · "}
                  Revenue: {analysis.funnel.revenue > 0 ? `$${analysis.funnel.revenue.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "N/A"}
                </p>
              </div>
            </div>

            <TrapezoidFunnel row={analysis.funnel} />

            {/* Cost metrics row */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Spend",          value: analysis.funnel.spend > 0 ? `$${analysis.funnel.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "N/A" },
                { label: "Cost / Opt-in",  value: fmtCur(analysis.funnel.costPerOptin) },
                { label: "Cost / App",     value: fmtCur(analysis.funnel.costPerApp) },
                { label: "Revenue",        value: analysis.funnel.revenue > 0 ? fmtCurK(analysis.funnel.revenue) : "N/A" },
              ].map(m => (
                <div
                  key={m.label}
                  className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 flex flex-col gap-0.5"
                >
                  <span className="text-[9px] text-muted-foreground uppercase tracking-widest font-semibold">{m.label}</span>
                  <span className="text-[14px] font-bold" style={{ color: analysis.funnel.color }}>{m.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── SECTION 2: Insights ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest px-2">
                Creative Performance Insights
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* KPI strip */}
            <KpiStrip row={analysis.funnel} />

            {/* Benchmark table */}
            <BenchmarkTable
              creative={analysis.funnel}
              accountAvg={analysis.accountAvg}
              typeAvg={analysis.typeAvg}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TOP 8 GRID MODE
// ─────────────────────────────────────────────
const TOP8_STAGES = FUNNEL_STAGES;

function Top8Grid({ topRows }: { topRows: IndividualCreativeFunnelRow[] }) {
  const maxImpressions = Math.max(...topRows.map(r => r.impressions), 1);

  function barWidth(row: IndividualCreativeFunnelRow, stageKey: StageKey): number {
    if (maxImpressions === 0 || row.impressions === 0) return 0;
    const val = row[stageKey] as number;
    const pct = (row.impressions / maxImpressions) * 80; // impression bar relative width (max 80%)
    if (stageKey === "impressions") return pct;
    return (val / row.impressions) * pct;
  }

  if (topRows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        No ads found for the current filters.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Scrollable funnel grid */}
      <div className="rounded-xl border border-border bg-card p-5 overflow-x-auto">
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${topRows.length}, minmax(100px, 1fr))`,
            minWidth: `${topRows.length * 110}px`,
          }}
        >
          {topRows.map(row => (
            <div key={row.adId} className="flex flex-col items-center gap-0">
              {/* Ad label */}
              <div className="flex flex-col items-center gap-1 mb-3">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: row.color }} />
                <span
                  className="text-[9px] font-bold text-center leading-tight line-clamp-2 max-w-[90px]"
                  style={{ color: row.color }}
                  title={row.adName}
                >
                  {row.shortName}
                </span>
              </div>

              {/* Funnel bars */}
              <div className="w-full flex flex-col items-center gap-0">
                {TOP8_STAGES.map((stage, i) => {
                  const prevStage = i > 0 ? TOP8_STAGES[i - 1] : null;
                  const rateInfo  = i > 0 ? RATE_BETWEEN[i - 1] : null;
                  const rateVal   = rateInfo ? (row[rateInfo.rateKey] as number) : null;
                  const width     = barWidth(row, stage.key);
                  const count     = row[stage.key] as number;
                  void prevStage; // suppress unused warning

                  return (
                    <React.Fragment key={stage.key}>
                      {rateInfo && rateVal !== null && (
                        <div className="py-0.5 flex justify-center">
                          <span
                            className="text-[8px] font-bold"
                            style={{ color: row.color }}
                          >
                            {fmtPct(rateVal)}
                          </span>
                        </div>
                      )}
                      <div className="w-full flex flex-col items-center gap-0.5">
                        <div className="w-full flex justify-center">
                          <div
                            className="h-5 rounded-sm"
                            style={{
                              width:    `${Math.max(width, width > 0 ? 6 : 0)}%`,
                              background: row.color,
                              opacity:  0.85 - i * 0.12,
                              minWidth: count > 0 ? "8px" : "0px",
                            }}
                          />
                        </div>
                        <span className="text-[8px] font-semibold text-foreground">{fmtCount(count)}</span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Mini cost metrics */}
              <div className="mt-3 w-full rounded-md border border-border/50 bg-muted/20 p-1.5 flex flex-col gap-1">
                <div>
                  <span className="text-[7px] text-muted-foreground uppercase tracking-widest block">Cost/Optin</span>
                  <span className="text-[10px] font-bold" style={{ color: row.color }}>{fmtCur(row.costPerOptin)}</span>
                </div>
                <div>
                  <span className="text-[7px] text-muted-foreground uppercase tracking-widest block">Cost/App</span>
                  <span className="text-[10px] font-bold" style={{ color: row.color }}>{fmtCur(row.costPerApp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Efficiency table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
            Top 8 Efficiency Summary
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Ad</th>
                {["CTR", "Opt-in Rate", "App Rate", "Close Rate", "Cost/Optin", "Cost/App", "Spend"].map(h => (
                  <th key={h} className="px-3 py-2.5 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topRows.map((row, i) => (
                <tr key={row.adId} className={i < topRows.length - 1 ? "border-b border-border/40" : ""}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: row.color }} />
                      <span className="text-[11px] font-semibold text-foreground max-w-[140px] truncate" title={row.adName}>
                        {row.shortName}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-foreground">{fmtPct(row.ctr)}</td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-foreground">{fmtPct(row.optinRate)}</td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-foreground">{fmtPct(row.appRate)}</td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-foreground">{fmtPct(row.closeRate)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className="text-[11px] font-bold" style={{ color: row.color }}>{fmtCur(row.costPerOptin)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <span className="text-[11px] font-bold" style={{ color: row.color }}>{fmtCur(row.costPerApp)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-muted-foreground">
                    {row.spend > 0 ? `$${row.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN SECTION
// ─────────────────────────────────────────────
interface IndividualCreativeFunnelSectionProps {
  rows:               MetaLeadGenRow[];
  filters:            MetaLeadGenFilters;
  closeAttribution?:  import("../useCloseCreativeAttribution").CloseCreativeMap;
}

export function IndividualCreativeFunnelSection({ rows, filters, closeAttribution }: IndividualCreativeFunnelSectionProps) {
  const [mode, setMode] = useState<"single" | "top8">("single");

  const adOptions = useMemo(() => getAdOptions(rows, filters), [rows, filters]);
  const top8Rows  = useMemo(() => getTopNAdsData(rows, filters, 8, closeAttribution), [rows, filters, closeAttribution]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h3 className="text-base font-bold text-foreground">Individual Creative Funnel</h3>
          <p className="text-sm text-muted-foreground">
            Drill into a single ad&apos;s funnel performance, or compare the top 8.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex items-center gap-1 p-1 rounded-lg border border-border bg-muted/30">
          {(["single", "top8"] as const).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-150 ${
                mode === m
                  ? "bg-[var(--brand-primary)] text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m === "single" ? "SINGLE" : "ALL TOP 8"}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {mode === "single" ? (
        <SingleMode adOptions={adOptions} rows={rows} filters={filters} closeAttribution={closeAttribution} />
      ) : (
        <Top8Grid topRows={top8Rows} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// INTERNAL SAFE DIVIDE (avoid import cycle)
// ─────────────────────────────────────────────
function safeDivide(n: number, d: number): number {
  if (d === 0 || !isFinite(d) || !isFinite(n)) return 0;
  const r = n / d;
  return isFinite(r) ? r : 0;
}
